import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { AsyncSearch } from 'components';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import SuccessDialog from 'components/organization/dialog/success-dialog';
import ControlledDataTable from 'components/tables/controlled-data-table';
import { DataTableRef } from 'components/tables/data-table';
import { ControlledDataTableProvider } from 'components/tables/data-table-context';
import { useAuth } from 'contexts';
import useConfirm from 'hooks/use-confirm';
import { useCustomerAPI } from 'hooks/use-customer-api';
import useCustomerCarrierApi, { CustomerCarrier } from 'hooks/use-customer-carrier-api';
import { useSearchParams } from 'react-router-dom';
import { getTruckingCompanyById, searchTruckingCompanies } from 'services';
import { Customer, Role, TruckingCompany } from 'types';
import { T_DataTableColumn } from 'types/data-table';
import { Filter, FilterOperator, FilterState, LogicalOperator } from 'utils';
import { formatTruckingCompany } from 'utils/format';

class CustomerCarrierColumns {
  id: T_DataTableColumn<CustomerCarrier, 'number'> = {
    field: 'id',
    label: 'ID',
    type: 'number',
    width: 48
  };
  createdBy: T_DataTableColumn<CustomerCarrier, 'text'> = {
    allowedRoles: [Role.ADMIN, Role.BILLING_ADMIN],
    field: 'createdBy',
    label: 'USER',
    type: 'text',
    width: 48
  };
  createdDate: T_DataTableColumn<CustomerCarrier, 'dateTime'> = {
    field: 'createdDate',
    dateFormat: 'l',
    label: 'DATE',
    type: 'dateTime',
    width: 48
  };
  startDate: T_DataTableColumn<CustomerCarrier, 'dateTime'> = {
    field: 'startDate',
    dateFormat: 'l',
    label: 'START',
    type: 'dateTime',
    width: 48
  };
  customer: T_DataTableColumn<CustomerCarrier, 'customer'> = {
    field: 'customer',
    getValueLabel: customer => customer?.name,
    label: 'ACCOUNT',
    type: 'customer',
    width: 64
  };
  truckingCompany: T_DataTableColumn<CustomerCarrier, 'truckingCompany'> = {
    field: 'truckingCompany',
    getValueLabel: truckingCompany => `${truckingCompany?.name ?? 'N/A'} (${truckingCompany?.scacCode ?? 'N/A'})`,
    label: 'CARRIER',
    type: 'truckingCompany',
    width: 64
  };
  gateInAllowed: T_DataTableColumn<CustomerCarrier, 'boolean'> = {
    field: 'gateInAllowed',
    getValueLabel: allowed => (allowed ? 'ALLOW' : 'STOP'),
    label: 'GATE-IN',
    type: 'boolean',
    width: 64
  };
  gateOutAllowed: T_DataTableColumn<CustomerCarrier, 'boolean'> = {
    field: 'gateOutAllowed',
    getValueLabel: allowed => (allowed ? 'ALLOW' : 'STOP'),
    label: 'GATE-OUT',
    type: 'boolean',
    width: 64
  };
  notes?: T_DataTableColumn<CustomerCarrier, 'text'> = {
    field: 'notes',
    label: 'NOTES',
    type: 'text',
    width: 96
  };
}

const CustomerRules: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { userProfile } = useAuth();
  const { getConfirmation, ConfirmationDialog } = useConfirm();
  const { searchCustomers } = useCustomerAPI();
  const { deleteRule, fetchData } = useCustomerCarrierApi();

  const dataTableRef = useRef<DataTableRef>(null);
  const columns = useMemo(() => Object.values(new CustomerCarrierColumns()), []);
  const initialColumns = useMemo(() => Object.keys(new CustomerCarrierColumns()).slice(1), []);

  const [customer, setCustomer] = useState<Customer>();
  const [truckingCompany, setTruckingCompany] = useState<TruckingCompany | null>();

  useEffect(() => {
    if (userProfile.role === Role.ADMIN_CUSTOMER && userProfile.customers?.length === 1 && !customer) {
      setCustomer(userProfile.customers[0]);
    }
  }, [userProfile.role, userProfile.customers, customer]);

  useEffect(() => {
    const truckingCompanyId = Number(searchParams.get('carrier'));
    if (truckingCompanyId) {
      getTruckingCompanyById(truckingCompanyId).then(setTruckingCompany);
    }
  }, [searchParams]);

  const handleSearchCustomers = useCallback(
    async (input: string) => {
      if (userProfile.role === Role.ADMIN_CUSTOMER) {
        const allowed = userProfile.customers ?? [];
        return input ? allowed.filter(c => c.name?.toLowerCase().includes(input.toLowerCase())) : allowed;
      }
      return searchCustomers(input);
    },
    [searchCustomers, userProfile.role, userProfile.customers]
  );

  const filterState: FilterState = useMemo(() => {
    const filterOperator = FilterOperator.EQUALS;
    const filters: Filter[] = [];
    if (truckingCompany) filters.push({ field: 'truckingCompany.id', filterOperator, value: truckingCompany.id });
    if (customer) {
      filters.push({ field: 'customer.id', filterOperator, value: customer.id });
    } else if (userProfile.role === Role.ADMIN_CUSTOMER && userProfile.customers?.length === 1) {
      filters.push({ field: 'customer.id', filterOperator, value: userProfile.customers[0].id });
    }
    return { logicalOperator: LogicalOperator.AND, filters };
  }, [customer, truckingCompany, userProfile.role, userProfile.customers]);

  const handleDelete = useCallback(
    async (rule: CustomerCarrier) => {
      const confirm = await getConfirmation(
        'Delete rule?',
        `Account - ${rule.customer?.name}\nCarrier - ${formatTruckingCompany(rule.truckingCompany)}`
      );
      if (confirm) {
        await deleteRule(rule.id);
        dataTableRef.current?.forceFetch();
      }
    },
    [deleteRule, getConfirmation]
  );

  const handleEdit = useCallback(
    (rule?: CustomerCarrier | null) => rule?.id && navigate(`/scac-management/account-rules/${rule.id}`),
    [navigate]
  );

  const uid = useRef(0);
  const handleReset = useCallback(() => {
    if (userProfile.role === Role.ADMIN_CUSTOMER && userProfile.customers?.length === 1) {
      setCustomer(userProfile.customers[0]);
    } else {
      setCustomer(undefined);
    }
    setTruckingCompany(undefined);
    uid.current++;
  }, [setCustomer, setTruckingCompany, userProfile.role, userProfile.customers]);

  return (
    <>
      <ConfirmationDialog danger />
      <ErrorDialog />
      <SuccessDialog />
      <ControlledDataTableProvider<CustomerCarrier> initialColumns={initialColumns} filterState={filterState}>
        <ControlledDataTable<CustomerCarrier>
          columns={columns}
          dataTableRef={dataTableRef}
          fetchData={fetchData}
          filterByLocation
          handleDelete={handleDelete}
          handleEdit={handleEdit}
          tableToolbar
        >
          <div className="grid-3" key={uid.current}>
            <AsyncSearch<Customer>
              label="ACCOUNT"
              formatValue={customer => customer?.name ?? ''}
              handleSelect={setCustomer}
              search={handleSearchCustomers}
              value={customer}
              data-cy="scac-management-account-search"
            />
            <AsyncSearch<TruckingCompany>
              label="CARRIER"
              formatValue={formatTruckingCompany}
              handleSelect={setTruckingCompany}
              search={searchTruckingCompanies}
              value={truckingCompany}
              data-cy="scac-management-carrier-search"
            />
            <div className="align-self-center">
              <button className="btn-close d-flex g-2 align-items-center" onClick={handleReset} title="clear filters">
                <span className={`material-icons icon-large filters-${filterState.filters?.length}`}>
                  filter_alt_off
                </span>
              </button>
            </div>
          </div>
          <button
            className="btn-primary"
            onClick={() => navigate('/scac-management/account-rules/new')}
            data-cy="scac-management-add-account-rule-button"
          >
            ADD ACCOUNT RULE
          </button>
        </ControlledDataTable>
      </ControlledDataTableProvider>
    </>
  );
};

export default CustomerRules;
